/**
 * RtcStore - Refactored to use Service Layer
 *
 * This store now contains ONLY:
 * - Observable state
 * - Service initialization
 * - Simple delegation methods
 * - UI-specific canvas event handlers
 *
 * All business logic moved to services/
 */

import {action, computed, makeObservable, observable, ObservableMap, reaction, runInAction} from "mobx";
import {isIOS, isMacOs} from "react-device-detect";
import {RtcService} from '../Components/RemoteSupport/services';
import {
    toolsEnum,
    screenOrientationEnum,
    toolsColors,
    messageTypeEnum,
    messageDataTypeEnum,
    rtcSteps
} from '../Components/RemoteSupport/core';
import sharedUtils from "../Utility";
import {getSortedCameras} from "../Components/RemoteSupport/helpers/CameraHelpers";
import Utility from "../Utility";
import {SurveyTypeEnum as surveyTypeEnum, RecordingStatusEnum} from "../serverClientShared/sharedEnums";

// Helper function for touch events
function disableTouchIfNotButton(event) {
    const className = event.target?.className;
    const notAllowedClasses = [
        "remote-canvas",
        "preview-canvas",
        'remote-support-container',
        'meeting-container',
        'big-window-container'
    ];
    const shouldPrevent = notAllowedClasses.reduce((previousValue, currentValue) =>
        previousValue || className?.includes(currentValue), false);
    if (shouldPrevent) {
        event.preventDefault();
    }
}

function toggleDragToRefresh(dragToRefreshIsOn, isDraw) {
    const bodyElement = document.getElementsByTagName('body')[0];
    bodyElement.style['overscroll-behavior-y'] = dragToRefreshIsOn ? '' : 'contain';
    if (isIOS) {
        bodyElement.style['overflow'] = dragToRefreshIsOn ? '' : 'hidden';
        if (!dragToRefreshIsOn && isDraw) {
            window.addEventListener("touchstart", disableTouchIfNotButton, {passive: false});
        } else {
            window.removeEventListener("touchstart", disableTouchIfNotButton);
        }
    }
}

// Re-export enums for backward compatibility with existing components
export {
    toolsColors,
    toolsEnum,
    messageTypeEnum,
    messageDataTypeEnum,
    rtcSteps
};

export class RtcStore {
    // ========================================================================
    // OBSERVABLE STATE
    // ========================================================================
    sessionObject = {};
    participantsMicStates = new ObservableMap();
    isCameraWasOn = false;
    orientation = window.screen.height > window.screen.width ? screenOrientationEnum.PORTRAIT : screenOrientationEnum.LANDSCAPE;
    cameraHeight = (isIOS || isMacOs ? (window.orientation === 0 || window.orientation === 180) : window.screen.orientation.type.includes('portrait')) ? 640 : 480;
    cameraWidth = (isIOS || isMacOs ? (window.orientation === 0 || window.orientation === 180) : window.screen.orientation.type.includes('portrait')) ? 480 : 640;
    messages = [];
    newMessage = false;
    unReadMessages = 0;
    drawPosition = null;
    activeStep = 'choose_channel';
    cameraOn = true;
    shareScreenOn = false;
    transcriptionOn = false;
    transcriptionLanguage = '';
    micOn = true;
    name = '';
    isGuest = false;
    userDetails = {};
    isChatOpen = false;
    facingMode = 'user';
    micChecked = false;
    isUsersOpen = false;
    selectedCamera = '';
    selectedMic = isIOS ? '' : 'default';
    selectedSpeaker = 'default';
    forcedFacingMode = null;
    roomId = '';
    sampleRate = 48000;
    translationRecognizer = null;
    transcriptionToken = '';
    transcriptionRegion = '';
    transcriptionLanguages = {};
    transcriptionMessages = [];
    mediaSocket = null;
    frozenFrame = null;
    remoteFreeze = false;
    remoteUnfreeze = false;
    framesMap = new Map();
    deviceId = '';
    deviceIdForTranscription = '';
    isNextFrameKey = '';
    password = '';
    audioTrack = null;
    videoTrack = null;
    cameraTrack = null;
    screenShareTrack = null;
    rtcStream = null;
    canvasVideoTrack = null;
    participants = {};
    pingInterval = null;
    transcriptionMessagesInterval = null;
    heartBeatInterval = null;
    frameNumber = 0;
    videoEncoder = null;
    playAudioInterval = null;
    roomActivityInterval = null;
    drawCanvasContext = null;
    streamCanvasContext = null;
    drawsHistory = [];
    noDrawsCanvasImage = null;
    noDrawsCanvasFrozenImage = null;
    previewCanvasesContexts = {};
    aspectRatio = 1;
    markerNumber = 0;
    selectedTool = toolsEnum.MARKER;
    selectedToolColor = toolsColors[0];
    selectedToolOn = false;
    drawPositionId = null;
    biggerWindowConnectionId = null;
    activeParticipantsTimes = {};
    activeParticipants = {};
    activeVideosTimes = {};
    activeVideos = {};
    activeAudiosTimes = {};
    audioChunk = [];
    activeAudios = {};
    freeze = false;
    room = {};
    latestFramesObj = {};
    participantsMap = new ObservableMap();
    canvasRefs = [];
    bigCanvasRef = null;
    emailToInvite = null;
    deviceIdInvite = null;
    areCanvasListenersAttached = false;
    retryCount = 0;

    outputAudioCtx = null;
    cameras = null
    backCameras = null
    frontCameras = null
    recordingInitiatorUserId = '';
    recordingStatus = '';
    recordingStartTimestamp = 0;
    recordingEndTimestamp = 0;
    // ========================================================================
    // CONSTRUCTOR
    // ========================================================================
    constructor(modalStore, surveyStore) {
        makeObservable(this, {
            sessionObject: observable,
            participantsMicStates: observable,
            isCameraWasOn: observable,
            newMessage: observable,
            retryCount: observable,
            messages: observable,
            unReadMessages: observable,
            drawPosition: observable,
            activeStep: observable,
            areCanvasListenersAttached: observable,
            cameraOn: observable,
            shareScreenOn: observable,
            transcriptionOn: observable,
            transcriptionLanguage: observable,
            micOn: observable,
            name: observable,
            isChatOpen: observable,
            micChecked: observable,
            facingMode: observable,
            isUsersOpen: observable,
            selectedCamera: observable,
            selectedMic: observable,
            selectedSpeaker: observable,
            forcedFacingMode: observable,
            roomId: observable,
            sampleRate: observable,
            translationRecognizer: observable,
            transcriptionToken: observable,
            transcriptionRegion: observable,
            transcriptionLanguages: observable,
            transcriptionMessages: observable,
            mediaSocket: observable,
            deviceId: observable,
            deviceIdForTranscription: observable,
            isNextFrameKey: observable,
            password: observable,
            audioTrack: observable,
            videoTrack: observable,
            rtcStream: observable,
            canvasVideoTrack: observable,
            pingInterval: observable,
            transcriptionMessagesInterval: observable,
            heartBeatInterval: observable,
            frameNumber: observable,
            videoEncoder: observable,
            playAudioInterval: observable,
            roomActivityInterval: observable,
            participants: observable,
            drawsHistory: observable,
            noDrawsCanvasImage: observable,
            noDrawsCanvasFrozenImage: observable,
            drawCanvasContext: observable,
            streamCanvasContext: observable,
            previewCanvasesContexts: observable,
            markerNumber: observable,
            selectedTool: observable,
            selectedToolOn: observable,
            selectedToolColor: observable,
            drawPositionId: observable,
            biggerWindowConnectionId: observable,
            activeParticipants: observable,
            activeParticipantsTimes: observable,
            activeVideosTimes: observable,
            activeVideos: observable,
            activeAudiosTimes: observable,
            audioChunk: observable,
            activeAudios: observable,
            isGuest: observable,
            userDetails: observable,
            freeze: observable,
            cameraWidth: observable,
            cameraHeight: observable,
            orientation: observable,
            room: observable,
            frozenFrame: observable,
            framesMap: observable,
            remoteFreeze: observable,
            remoteUnfreeze: observable,
            latestFramesObj: observable,
            participantsMap: observable,
            canvasRefs: observable,
            bigCanvasRef: observable,
            emailToInvite: observable,
            deviceIdInvite: observable,
            cameras: observable,
            frontCameras: observable,
            backCameras: observable,
            recordingStatus: observable,
            recordingStartTimestamp: observable,
            recordingEndTimestamp: observable,
            recordingInitiatorUserId: observable,
            // computed
            cameraSide: computed,
            isRecordingInitiator: computed,
            isRecordingActive: computed,

            // Action methods
            toggleCamera: action,
            toggleShareScreen: action,
            toggleMic: action,
            toggleTranscription: action,
            closeCall: action,
            toggleChatOpen: action,
            toggleUsersOpen: action,
            joinCall: action,
            createSession: action,
            closeSession: action,
            inviteUser: action,
            clearLast: action,
            clearAll: action,
            isBigVideoSelf: action,
            setMouseDown: action,
            setMouseUp: action,
            drawOnCanvas: action,
            markOnCanvas: action,
            ping: action,
            switchCameraSide: action,
            handleAndroidCameraRotation: action,
            handleAndroidDualStreams: action,
            handleFacingModeToggle: action,
            handleMaximize: action,
        });

        // Initialize RTC Service (NEW!)
        this.modalStore = modalStore;
        this.surveyStore = surveyStore;
        this.rtcService = new RtcService(this);

        // Initialize canvas event listeners
        this.registerLocalMousePaintingEvents();
        this.adjustCameraDimensions();

        // Handle orientation changes
        window.onorientationchange = (event) => {
            const drawingService = this.rtcService.getDrawingService();
            drawingService.addToHistory(this.drawCanvasContext);

            const isPortrait = event.target.screen.orientation.type.includes('portrait');
            if (isPortrait) {
                this.cameraHeight = 640;
                this.cameraWidth = 480;
                this.orientation = screenOrientationEnum.PORTRAIT;
            } else {
                this.cameraHeight = 480;
                this.cameraWidth = 640;
                this.orientation = screenOrientationEnum.LANDSCAPE;
            }
        };

        this.setupRecordingCleanup();
    }

    // ========================================================================
    // CAMERA DIMENSION HELPERS
    // ========================================================================
    adjustCameraDimensions() {
        const aspectRatio = 4 / 3;
        const screenAspectRatio = window.innerWidth / window.innerHeight;

        if (screenAspectRatio > aspectRatio) {
            this.cameraHeight = window.innerHeight;
            this.cameraWidth = window.innerHeight * aspectRatio;
        } else {
            this.cameraWidth = window.innerWidth;
            this.cameraHeight = window.innerWidth / aspectRatio;
        }
    }

    // ========================================================================
    // CANVAS EVENT LISTENERS (UI-specific, stays in store)
    // ========================================================================
    registerLocalMousePaintingEvents = () => {
        const onListenerChange = (selectedToolOn) => {
            if (selectedToolOn) {
                toggleDragToRefresh(false, this.selectedTool === toolsEnum.DRAW);
                if (this.bigCanvasRef?.current) {
                    this.bigCanvasRef.current.addEventListener('mousedown', this.setMouseDown);
                    this.bigCanvasRef.current.addEventListener('touchstart', this.setMouseDown);
                    this.bigCanvasRef.current.addEventListener('mouseup', this.setMouseUp);
                    this.bigCanvasRef.current.addEventListener('touchend', this.setMouseUp);
                    this.bigCanvasRef.current.addEventListener('mousemove', this.drawOnCanvas);
                    this.bigCanvasRef.current.addEventListener('touchmove', this.drawOnCanvas);
                    this.bigCanvasRef.current.addEventListener('click', this.markOnCanvas);
                }
            } else {
                toggleDragToRefresh(true);
                if (this.bigCanvasRef?.current) {
                    this.bigCanvasRef.current.removeEventListener('mousedown', this.setMouseDown);
                    this.bigCanvasRef.current.removeEventListener('touchstart', this.setMouseDown);
                    this.bigCanvasRef.current.removeEventListener('mouseup', this.setMouseUp);
                    this.bigCanvasRef.current.removeEventListener('touchend', this.setMouseUp);
                    this.bigCanvasRef.current.removeEventListener('mousemove', this.drawOnCanvas);
                    this.bigCanvasRef.current.removeEventListener('touchmove', this.drawOnCanvas);
                    this.bigCanvasRef.current.removeEventListener('click', this.markOnCanvas);
                }
            }
        };

        reaction(() => this.selectedToolOn, selectedToolOn => onListenerChange(selectedToolOn));
        reaction(() => this.biggerWindowConnectionId, () => {
            setTimeout(() => onListenerChange(this.selectedToolOn), 300);
            this.rtcService.getStreamService().requestKeyFrame(this.biggerWindowConnectionId);
        });
        reaction(() => this.selectedTool, () => onListenerChange(this.selectedToolOn));
        reaction(() => this.transcriptionLanguage, () => {
            if (this.transcriptionLanguage) {
                this.rtcService.getTranscriptionService().startTranscription();
            }
        });
        reaction(() => this.orientation, () => {
            const canvasImage = this.drawsHistory.pop();
            if (canvasImage) {
                this.drawCanvasContext.putImageData(canvasImage, 0, 0);
                this.isNextFrameKey = true;
            }
        });
        reaction(() => this.selectedCamera, () => {
            this.isNextFrameKey = true;
        });
        reaction(() => this.shareScreenOn, () => {
            if (this.shareScreenOn) {
                this.isNextFrameKey = true;
                this.rtcService.getStreamService().startVideoSending();
            }
        });
        reaction(() => this.cameraOn, () => {
            if (this.cameraOn) {
                this.isNextFrameKey = true;
                this.rtcService.getStreamService().startVideoSending();
            }
        });
        reaction(() => this.selectedMic, () => {
            this.rtcService.getMediaService().registerAudioInput();
        });
        reaction(() => this.selectedSpeaker, () => {
            if (this.selectedSpeaker && this.outputAudioCtx) {
                const speakerId = this.selectedSpeaker === 'default' ? '' : this.selectedSpeaker;
                this.outputAudioCtx.setSinkId(speakerId);
            }
        });
        reaction(() => this.remoteFreeze, () => {
            if (!this.remoteFreeze) {
                this.rtcService.getStreamService().requestKeyFrame(this.biggerWindowConnectionId);
            }
        });
    };

    setMouseDown = () => {
        const {v4: uuidv4} = require("uuid");
        this.drawPositionId = uuidv4();
    };

    setMouseUp = () => {
        this.drawPositionId = null;
    };

    // ========================================================================
    // DELEGATED METHODS (Use services)
    // ========================================================================

    // Camera
    toggleCamera = () => {
        this.cameraOn = !this.cameraOn;
        if (this.shareScreenOn) {
            this.shareScreenOn = false;
        }
    };

    // Screen Share
    toggleShareScreen = () => {
        this.shareScreenOn = !this.shareScreenOn;
        if (this.cameraOn) {
            this.cameraOn = false;
        }
    };

    // Microphone
    toggleMic = () => {
        this.micOn = !this.micOn;
        this.rtcService.getParticipantService().broadcastMicState();

        if (this.translationRecognizer) {
            if (this.micOn) {
                this.translationRecognizer.startContinuousRecognitionAsync();
            } else {
                this.translationRecognizer.stopContinuousRecognitionAsync();
            }
        }
    };

    registerAudioInput = () => {
        const mediaService = this.rtcService.getMediaService();
        mediaService.registerAudioInput();
    };

    unregisterAudioInput = () => {
        const mediaService = this.rtcService.getMediaService();
        mediaService.unregisterAudioInput();
    };

    cleanupAudioTracks = () => {
        if (this.audioTrack) {
            this.rtcStream?.removeTrack(this.audioTrack);
            this.audioTrack.stop();
            this.audioTrack = null;
        }
    };

    cleanupVideoTracks = () => {
        if (this.cameraTrack) {
            this.rtcStream?.removeTrack(this.cameraTrack);
            this.cameraTrack.stop();
            this.cameraTrack = null;
        }
    };

    cleanupScreenShareTracks = () => {
        if (this.screenShareTrack) {
            this.screenShareTrack.stop();
            this.screenShareTrack = null;
        }
    };

    changeToBackCamera = async () => {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(device => device.kind === 'videoinput');
        cameras.sort((a, b) => {
            const numA = parseInt(a.label.match(/camera2\s+(\d+)/)[1]);
            const numB = parseInt(b.label.match(/camera2\s+(\d+)/)[1]);
            return numA - numB;
        });
        const backCamera = cameras.find(camera => camera.label.includes('back'));
        this.selectedCamera = backCamera?.deviceId;
    };

    // Transcription
    toggleTranscription = async () => {
        this.transcriptionOn = !this.transcriptionOn;
        const transcriptionService = this.rtcService.getTranscriptionService();

        if (!this.translationRecognizer && this.transcriptionOn) {
            if (!this.transcriptionLanguage) {
                const {generalStore} = require("../index");
                const {languageEnum, preferredLanguageEnum} = require("../serverClientShared/sharedEnums");

                if (generalStore?.userDetails?.PreferredLanguage) {
                    const language = Object.keys(languageEnum).find(key =>
                        languageEnum[key] === generalStore.userDetails.PreferredLanguage
                    );
                    this.transcriptionLanguage = preferredLanguageEnum[language];
                } else {
                    this.transcriptionLanguage = 'en-US';
                }
            }
            await transcriptionService.startTranscription();
            transcriptionService.requestTranscriptionFromPeers();
        }
    };

    // Chat
    toggleChatOpen = () => {
        this.isChatOpen = !this.isChatOpen;
        this.isUsersOpen = false;
        if (this.isChatOpen) {
            this.unReadMessages = 0;
        }
    };

    toggleUsersOpen = () => {
        this.isUsersOpen = !this.isUsersOpen;
        this.isChatOpen = false;
    };

    // Drawing
    clearAll = (isRemote) => {
        this.rtcService.getDrawingService().clearAll(isRemote);
    };

    clearLast = (isRemote) => {
        this.rtcService.getDrawingService().clearLast(isRemote);
    };

    finishDrawingOnRemote = () => {
        this.noDrawsCanvasFrozenImage = null;
        this.drawCanvasContext?.clearRect(0, 0, this.cameraWidth, this.cameraHeight);
        this.frozenFrame = null;
        this.rtcService.getStreamService().requestKeyFrame(this.biggerWindowConnectionId);
        this.selectedToolOn = false;
    };

    sendFreezeToRemote = () => {
        this.rtcService.getDrawingService().sendFreezeToRemote();
    };

    drawOnCanvas = (event) => {
        const drawingService = this.rtcService.getDrawingService();
        const isBigVideoSelf = this.isBigVideoSelf();

        if (!this.drawPositionId || this.selectedTool !== toolsEnum.DRAW) {
            return;
        }

        let {offsetX: bigX, offsetY: bigY} = event;
        const rect = event.target.getBoundingClientRect();

        if (!bigX) {
            bigX = event.targetTouches[0].pageX - rect.left;
        }
        if (!bigY) {
            bigY = event.targetTouches[0].pageY - rect.top;
            const isScreenShare = this.latestFramesObj[this.biggerWindowConnectionId]?.IsScreenShare;
            if (!isBigVideoSelf && !isScreenShare) {
                bigY = rect.height - bigY;
            }
        }

        if (isBigVideoSelf || this.remoteFreeze) {
            drawingService.drawOnSelf(bigX, bigY, 'draw', this.selectedToolColor);
            if (isBigVideoSelf) {
                return;
            }
        }

        if (!isBigVideoSelf || this.remoteFreeze) {
            const {markerTypeEnum} = require('../Components/RemoteSupport/core/constants/RtcEnums');
            drawingService.drawOrMarkToOthers(bigX, bigY, markerTypeEnum.DRAW);
        }
    };

    markOnCanvas = (event) => {
        const drawingService = this.rtcService.getDrawingService();
        const {markerTypeEnum} = require('../Components/RemoteSupport/core/constants/RtcEnums');

        let {offsetX: bigX, offsetY: bigY} = event;
        const rect = event.target.getBoundingClientRect();

        if (!bigX) {
            bigX = event.targetTouches[0].pageX - rect.left;
        }
        if (!bigY) {
            bigY = event.targetTouches[0].pageY - rect.top;
        }

        const isBigVideoSelf = this.isBigVideoSelf();

        if (isBigVideoSelf || this.remoteFreeze) {
            let toolType;
            switch (this.selectedTool) {
                case toolsEnum.MARKER:
                    toolType = 'marker';
                    break;
                case toolsEnum.BEACON:
                    toolType = 'beacon';
                    break;
                default:
                    return;
            }

            drawingService.drawOnSelf(bigX, bigY, toolType, this.selectedToolColor);

            if (isBigVideoSelf) {
                return;
            }
        }

        if (!isBigVideoSelf || this.remoteFreeze) {
            let markerType;
            switch (this.selectedTool) {
                case toolsEnum.MARKER:
                    markerType = markerTypeEnum.ARROW;
                    break;
                case toolsEnum.BEACON:
                    markerType = markerTypeEnum.BEACON;
                    break;
                default:
                    return;
            }

            drawingService.drawOrMarkToOthers(bigX, bigY, markerType);
        }
    };

    // Call Management
    joinCall = async () => {
        await this.rtcService.joinCall(this.roomId, this.password);
    };

    closeCall = async () => {
        console.log(this.roomId)
        await this.rtcService.leaveCall();
        this.surveyStore.currentSurvey = {surveyName: 'Remote Support', surveyType: surveyTypeEnum.REMOTE_SUPPORT_POST_CALL, surveyIdentifier: this.roomId}
        const isAnswered = await this.surveyStore.checkIfPermissionOrAnswerAlreadyExists()
        if (!isAnswered){
            this.modalStore.isSurveyPopupOpen = true;
        }
        this.activeStep = 'after_meeting';
        this.isUsersOpen = false;
        this.isChatOpen = false;
    };


    createSession = async () => {
        await this.rtcService.createSession();
    };

    closeSession = async () => {
        await this.rtcService.closeSession();

    };

    inviteUser = async (invitedUserId) => {
        await this.rtcService.inviteUser(invitedUserId);
    };

    ping = async () => {
        await this.rtcService.ping();
    };

    sendRoomMessage = (data, roomId) => {
        const socketManager = this.rtcService.getSocketManager();
        socketManager.sendRoomMessage(roomId, data, this.deviceId);
    };

    // Helpers
    isBigVideoSelf = () => {
        return this.biggerWindowConnectionId === this.deviceId;
    };

    isHoloLens = (senderId) => {
        if (!senderId && !this.biggerWindowConnectionId) {
            return false;
        }
        const drawingDeviceFirstFrame = this.latestFramesObj[senderId || this.biggerWindowConnectionId];
        if (!drawingDeviceFirstFrame) {
            return false;
        }
        return ['HL2', 'ML2'].includes(drawingDeviceFirstFrame.DeviceName);
    };

    isDrawOnAR = () => {
        const isFirstInRoom = this.getFirstSlot();
        const isHoloLens = this.isHoloLens();
        return isFirstInRoom && isHoloLens;
    };

    getFirstSlot = () => {
        const participants = this.participantsMap;
        if (participants.size === 0) {
            return false;
        }
        const firstParticipant = [...participants.values()][0];
        return firstParticipant?.deviceId === this.deviceId;
    };

    addReceivedMessage = (content) => {
        const name = sharedUtils.getUserFullName(content.Sender, true);
        const time = new Date(content.UTCTimestamp);
        const body = content.MessageText;
        const projectItemData = content.ProjectItemData;
        const messageDataType = content.MessageDataType;
        this.newMessage = true;
        this.messages.push({
            name, time, body, projectItemData,
            messageDataType, fromSelf: false
        });
        if (!this.isChatOpen) {
            this.unReadMessages++;
        }
    }

    async initCameras() {
        const {cameras, frontCameras, backCameras} = await getSortedCameras();
        this.cameras = cameras;
        this.frontCameras = frontCameras;
        this.backCameras = backCameras;
    }

    get cameraSide() {
        if (!this.cameras) {
            if (Utility.isTablet()) {
                return 'front';
            } else if (isIOS) {
                return this.forcedFacingMode === 'user' ? 'front' : 'back';
            }
            return 'back';
        }
        if (isIOS || (this.cameras.length === 1)) {
            return this.forcedFacingMode === 'user' ? 'front' : 'back';
        }
        if (this.selectedCamera && this.cameras.length >= 2) {
            if (this.backCameras && this.backCameras.some(c => c.deviceId === this.selectedCamera)) {
                return 'back';
            }
            if (this.frontCameras && this.frontCameras.some(c => c.deviceId === this.selectedCamera)) {
                return 'front';
            }
            const index = this.cameras.findIndex(c => c.deviceId === this.selectedCamera);
            return index === 0 ? 'front' : 'back';
        }
        return 'back';
    }

    async switchCameraSide() {
        if (!this.cameras)
            await this.initCameras()
        if (Utility.isAndroid()) {
            if (this.frontCameras.length && this.backCameras.length) {
                return this.handleAndroidDualStreams(this.frontCameras, this.backCameras);
            }
            if (this.cameras.length >= 2) {
                return this.handleAndroidCameraRotation(this.cameras);
            }
            if (this.cameras.length === 1) {
                return this.handleFacingModeToggle();
            }
            console.error('No cameras detected');
            return;
        }
        if (isIOS) {
            return this.handleFacingModeToggle();
        }
    }

    handleAndroidDualStreams(frontCameras, backCameras) {
        const currentIsBack = backCameras.find(c => c.deviceId === this.selectedCamera);
        if (currentIsBack) {
            this.selectedCamera = frontCameras[0].deviceId;
        } else {
            this.selectedCamera = backCameras[0].deviceId;
        }
    }

    handleAndroidCameraRotation(cameras,) {
        const currentIndex = cameras.findIndex(c => c.deviceId === this.selectedCamera) || 0;
        const nextIndex = (currentIndex + 1) % cameras.length;
        this.selectedCamera = cameras[nextIndex].deviceId;
    }

    handleFacingModeToggle() {
        const isUserFacing = this.forcedFacingMode === 'user' || !this.forcedFacingMode;
        if (isUserFacing) {
            this.forcedFacingMode = 'environment';
        } else {
            this.forcedFacingMode = 'user';
        }
    }

    handleMaximize(senderId) {
        this.biggerWindowConnectionId = senderId;
        if (this.isRecordingInitiator)
            this.rtcService.recordingService.maximizeUserForRecorder(senderId).then();
    }

    get isRecordingInitiator() {
        const {generalStore} = require("../index");
        return generalStore.userDetails._id === this.recordingInitiatorUserId;
    }

    get isRecordingActive() {
        return this.rtcService.recordingService.isStatusActive(this.recordingStatus)
    }

    setupRecordingCleanup() {
        reaction(
            () => this.recordingStatus,
            (status) => {
                if (status === RecordingStatusEnum.COMPLETED || status === RecordingStatusEnum.FINALIZING) {
                    setTimeout(() => {
                        runInAction(() => {
                            this.resetRecordingState();
                        });
                    }, 3000);
                }
            }
        );
    }

    resetRecordingState() {
        this.recordingInitiatorUserId = '';
        this.recordingStatus = '';
        this.recordingStartTimestamp = 0;
        this.recordingEndTimestamp = 0;
        this.rtcService.recordingService.recordingId = '';
        this.rtcService.recordingService.recordingSecret = '';
        this.rtcService.recordingService.recordingFileId = '';
        this.rtcService.recordingService.recordingUploadId = '';
    }
}
